import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { QRCodeCanvas } from 'qrcode.react';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

const SignupSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userInfo, setUserInfo] = useState(null);
  const [totpUri, setTotpUri] = useState('');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const verifyPaymentAndCreateUser = async () => {
      if (!sessionId) {
        setError('No session ID found');
        setLoading(false);
        return;
      }

      try {
        // Verify the payment session
        const verifyResponse = await fetch(`https://plansync-backend-234288438538.us-central1.run.app/api/stripe/verify-session/${sessionId}`);
        const verifyData = await verifyResponse.json();

        if (!verifyResponse.ok) {
          throw new Error(verifyData.detail || 'Failed to verify payment');
        }

        if (verifyData.payment_status !== 'paid') {
          throw new Error('Payment not completed');
        }

        // Payment successful - the webhook will create the user
        // We need to wait a moment for the webhook to process
        setTimeout(async () => {
          // Get user info from metadata
          const metadata = verifyData.metadata;
          
          setUserInfo({
            email: metadata.email,
            firstName: metadata.first_name,
            lastName: metadata.last_name
          });

          // Use the TOTP URI from the backend if available
          if (verifyData.totp_uri) {
            setTotpUri(verifyData.totp_uri);
          } else {
            // Fallback - try to get it again after a delay
            setTimeout(async () => {
              const retryResponse = await fetch(`https://plansync-backend-234288438538.us-central1.run.app/api/stripe/verify-session/${sessionId}`);
              const retryData = await retryResponse.json();
              if (retryData.totp_uri) {
                setTotpUri(retryData.totp_uri);
              }
            }, 3000);
          }
          
          setLoading(false);
        }, 2000); // Wait 2 seconds for webhook to process

      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    verifyPaymentAndCreateUser();
  }, [sessionId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-bl from-sky-400 to-blue-800">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-white rounded-3xl shadow-2xl p-8 max-w-md text-center"
        >
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-800">Processing your payment...</h2>
          <p className="text-gray-600 mt-2">Please wait while we set up your account</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-bl from-sky-400 to-blue-800">
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-2xl p-8 max-w-md"
        >
          <div className="text-center">
            <div className="text-red-500 text-5xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Payment Error</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/signup')}
              className="bg-blue-600 text-white px-6 py-2 rounded-full hover:bg-blue-700 transition-colors"
            >
              Back to Signup
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center min-h-screen bg-gradient-to-bl from-sky-400 to-blue-800">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-3xl shadow-2xl p-8 max-w-lg w-full"
      >
        {/* Success Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          className="flex justify-center mb-6"
        >
          <CheckCircleIcon className="h-20 w-20 text-green-500" />
        </motion.div>

        {/* Success Message */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Payment Successful!</h1>
          <p className="text-gray-600">
            Welcome to PlanSync AI, {userInfo?.firstName}! Your account has been created.
          </p>
        </div>

        {/* TOTP Setup */}
        <div className="bg-gray-50 rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4 text-center">Set Up Two-Factor Authentication</h2>
          <p className="text-sm text-gray-600 mb-4 text-center">
            Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
          </p>
          
          <div className="flex justify-center mb-4">
            <div className="bg-white p-4 rounded-lg shadow-inner">
              <QRCodeCanvas value={totpUri} size={200} />
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800 text-center">
              <strong>Important:</strong> Save this QR code before proceeding. You'll need the code from your authenticator app to log in.
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={() => navigate('/login')}
            className="w-full bg-gradient-to-bl from-sky-400 to-blue-800 text-white py-3 rounded-full font-semibold hover:opacity-90 transition-opacity"
          >
            Proceed to Login
          </button>
          
          <p className="text-center text-sm text-gray-500">
            You'll be asked for a 6-digit code from your authenticator app
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default SignupSuccess;